import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useMenu } from '../../context/MenuContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiCheck, FiX, FiSave, FiRefreshCw, FiToggleLeft, FiToggleRight, FiInfo, FiLoader, FiAlertCircle } = FiIcons;

const DayConfiguration = () => {
  const { availableDays, updateAvailableDays, loadAvailableDays } = useMenu();
  const [dayConfigs, setDayConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  // Alle möglichen Tage mit deutscher Übersetzung
  const allDays = [
    { key: 'montag', label: 'Montag', order: 1 },
    { key: 'dienstag', label: 'Dienstag', order: 2 },
    { key: 'mittwoch', label: 'Mittwoch', order: 3 },
    { key: 'donnerstag', label: 'Donnerstag', order: 4 },
    { key: 'freitag', label: 'Freitag', order: 5 },
    { key: 'samstag', label: 'Samstag', order: 6 },
    { key: 'sonntag', label: 'Sonntag', order: 7 }
  ];

  // Initialisiere die Konfiguration basierend auf den aktuell verfügbaren Tagen
  useEffect(() => {
    const configs = allDays.map(dayDef => ({
      day: dayDef.key,
      label: dayDef.label,
      active: availableDays.includes(dayDef.key),
      order: dayDef.order
    }));
    setDayConfigs(configs);
  }, [availableDays]);

  const handleToggleDay = (dayKey) => {
    setDayConfigs(prev =>
      prev.map(config =>
        config.day === dayKey
          ? { ...config, active: !config.active }
          : config
      )
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');

    try {
      // Überprüfe, ob mindestens ein Tag aktiv ist
      const activeDays = dayConfigs.filter(config => config.active);
      if (activeDays.length === 0) {
        throw new Error('Mindestens ein Tag muss aktiv sein.');
      }

      await updateAvailableDays(dayConfigs);
      
      setMessage('Tage-Konfiguration erfolgreich gespeichert!');
      setMessageType('success');

      // Nachricht nach 3 Sekunden ausblenden
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);

    } catch (error) {
      console.error('Error saving day configuration:', error);
      setMessage(error.message || 'Fehler beim Speichern der Konfiguration');
      setMessageType('error');

      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    setMessage('');

    try {
      await loadAvailableDays();
      setMessage('Konfiguration aktualisiert!');
      setMessageType('success');

      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 2000);
    } catch (error) {
      console.error('Error refreshing configuration:', error);
      setMessage('Fehler beim Aktualisieren der Konfiguration');
      setMessageType('error');

      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const activeDaysCount = dayConfigs.filter(config => config.active).length;

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Tage-Konfiguration</h1>
          <p className="text-gray-600">Verwalten Sie die verfügbaren Menü-Tage</p>
        </div>
        <div className="flex space-x-3">
          <motion.button
            onClick={handleRefresh}
            disabled={isLoading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`bg-gray-100 text-gray-700 px-4 py-2 rounded-xl font-semibold hover:bg-gray-200 transition-colors duration-300 flex items-center space-x-2 ${
              isLoading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            <SafeIcon icon={FiRefreshCw} className={isLoading ? 'animate-spin' : ''} />
            <span>Aktualisieren</span>
          </motion.button>
        </div>
      </div>

      {/* Status-Nachricht */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-xl flex items-center ${
            messageType === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          <SafeIcon 
            icon={messageType === 'success' ? FiCheck : FiAlertCircle} 
            className="mr-2" 
          />
          <span>{message}</span>
        </motion.div>
      )}

      {/* Übersicht */}
      <div className="mb-8 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-center space-x-2 text-blue-800 mb-2">
          <SafeIcon icon={FiInfo} />
          <span className="font-medium">Aktuelle Konfiguration:</span>
        </div>
        <p className="text-blue-700">
          <strong>{activeDaysCount} von {allDays.length} Tagen</strong> sind aktiviert.
        </p>
        <p className="text-blue-600 text-sm mt-1">
          Aktive Tage: {dayConfigs
            .filter(config => config.active)
            .map(config => config.label)
            .join(', ') || 'Keine'
          }
        </p>
      </div>

      {/* Tage-Konfiguration */}
      <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-6">Verfügbare Menü-Tage</h2>
        
        <div className="space-y-4">
          {dayConfigs.map((config) => (
            <motion.div
              key={config.day}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-300 ${
                config.active 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  config.active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                }`}>
                  <SafeIcon icon={FiCalendar} />
                </div>
                <div>
                  <h3 className={`font-semibold ${
                    config.active ? 'text-green-800' : 'text-gray-600'
                  }`}>
                    {config.label}
                  </h3>
                  <p className={`text-sm ${
                    config.active ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {config.active ? 'Menüs verfügbar' : 'Keine Menüs'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <span className={`text-sm font-medium ${
                  config.active ? 'text-green-700' : 'text-gray-500'
                }`}>
                  {config.active ? 'Aktiv' : 'Inaktiv'}
                </span>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleToggleDay(config.day)}
                  className={`p-2 rounded-lg transition-colors ${
                    config.active 
                      ? 'bg-green-200 text-green-700 hover:bg-green-300' 
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                >
                  <SafeIcon 
                    icon={config.active ? FiToggleRight : FiToggleLeft} 
                    className="text-2xl" 
                  />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Hinweise */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center space-x-2 text-amber-800 mb-2">
            <SafeIcon icon={FiInfo} />
            <span className="font-medium">Wichtige Hinweise:</span>
          </div>
          <ul className="text-amber-700 text-sm space-y-1">
            <li>• Mindestens ein Tag muss aktiv bleiben</li>
            <li>• Änderungen wirken sich auf neue Menü-Erstellungen aus</li>
            <li>• Bestehende Menüs bleiben von Änderungen unberührt</li>
            <li>• Die 5-Tage-Menü-Funktion verwendet nur aktive Tage</li>
          </ul>
        </div>

        {/* Speichern-Button */}
        <div className="mt-6 flex justify-end">
          <motion.button
            onClick={handleSave}
            disabled={isSaving || activeDaysCount === 0}
            whileHover={{ scale: isSaving || activeDaysCount === 0 ? 1 : 1.02 }}
            whileTap={{ scale: isSaving || activeDaysCount === 0 ? 1 : 0.98 }}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-colors duration-300 ${
              isSaving || activeDaysCount === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary-500 text-white hover:bg-primary-600'
            }`}
          >
            {isSaving ? (
              <>
                <SafeIcon icon={FiLoader} className="animate-spin" />
                <span>Wird gespeichert...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiSave} />
                <span>Konfiguration speichern</span>
              </>
            )}
          </motion.button>
        </div>
      </div>

      {/* Vorschau der Auswirkungen */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Vorschau der Änderungen</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-gray-700 mb-3">Aktive Tage (Menüs verfügbar):</h3>
            <div className="space-y-2">
              {dayConfigs
                .filter(config => config.active)
                .map(config => (
                  <div key={config.day} className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheck} className="text-green-600" />
                    <span className="text-green-700">{config.label}</span>
                  </div>
                ))
              }
              {activeDaysCount === 0 && (
                <p className="text-red-600 text-sm">Keine Tage ausgewählt</p>
              )}
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-700 mb-3">Inaktive Tage (keine Menüs):</h3>
            <div className="space-y-2">
              {dayConfigs
                .filter(config => !config.active)
                .map(config => (
                  <div key={config.day} className="flex items-center space-x-2">
                    <SafeIcon icon={FiX} className="text-red-600" />
                    <span className="text-red-600">{config.label}</span>
                  </div>
                ))
              }
              {activeDaysCount === allDays.length && (
                <p className="text-gray-500 text-sm">Alle Tage sind aktiv</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayConfiguration;